import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Filter,
  ClipboardList,
  UserPlus,
  XCircle,
  History,
  RefreshCw,
  GitBranch,
  PenSquare,
  Download,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader } from '../../components/PageHeader';
import { portfolioService } from '../../services/portfolio.service';
import TaxonomyCascade from '../../components/TaxonomyCascade';
import { useAuthStore } from '../../store/authStore';
import {
  editorialWorkflowService,
  ReviewTask,
  EditorialTaskState,
} from '../../services/editorial-workflow.service';

/**
 * Editorial Review Tasks (NWFE-RQ-01.1)
 * Every generated question becomes a task moving through Stage 1 (grammar)
 * and Stage 2 (conceptual) review. Manual assignment (Phase 1 decision).
 */

const STATE_LABELS: Record<EditorialTaskState, string> = {
  pending_stage_1: 'Pending Stage 1 — Grammar',
  pending_stage_2: 'Pending Stage 2 — Conceptual',
  reviewed: 'Reviewed',
  rejected: 'Rejected',
  published: 'Published',
};

const STATE_BADGE: Record<EditorialTaskState, string> = {
  pending_stage_1: 'bg-amber-50 border-amber-200 text-amber-700',
  pending_stage_2: 'bg-blue-50 border-blue-200 text-blue-700',
  reviewed: 'bg-green-50 border-green-200 text-green-700',
  rejected: 'bg-red-50 border-red-200 text-red-700',
  published: 'bg-purple-50 border-purple-200 text-purple-700',
};

const PAGE_SIZE = 25;

interface Filters {
  state: string;
  search: string;
  oeCode: string;
  type: string;
  difficulty: string;
  portfolio: string;
  taxonomyNode: string | null;
  mine: boolean;
}

const EMPTY_FILTERS: Filters = {
  state: '',
  search: '',
  oeCode: '',
  type: '',
  difficulty: '',
  portfolio: '',
  taxonomyNode: null,
  mine: false,
};

export function ReviewTasks() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const permissions: string[] = user?.permissions || [];

  const canAssign = permissions.includes('review_assign');
  const canStage1 = permissions.includes('review_stage_1');
  const canStage2 = permissions.includes('review_stage_2');

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const { data: config } = useQuery({
    queryKey: ['editorial-config'],
    queryFn: () => editorialWorkflowService.getConfig(),
    staleTime: 10 * 60 * 1000,
  });

  // Base-scope parity: portfolios for the filter dropdown + taxonomy cascade
  const { data: taskPortfolios = [] } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const filterPortfolioId =
    taskPortfolios.find((p) => p.name === filters.portfolio)?._id || null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['editorial-tasks', appliedFilters, page],
    queryFn: () =>
      editorialWorkflowService.listTasks({
        state: appliedFilters.state || undefined,
        search: appliedFilters.search || undefined,
        oeCode: appliedFilters.oeCode || undefined,
        type: appliedFilters.type || undefined,
        difficulty: appliedFilters.difficulty || undefined,
        portfolio: appliedFilters.portfolio || undefined,
        taxonomyNode: appliedFilters.taxonomyNode || undefined,
        mine: appliedFilters.mine || undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['editorial-tasks'] });
  };

  const backfillMutation = useMutation({
    mutationFn: () => editorialWorkflowService.backfillTasks(),
    onSuccess: (summary) => {
      invalidate();
      Swal.fire({
        title: 'Backfill complete',
        text: `${summary.created} task(s) created from ${summary.scanned} existing question(s).`,
        icon: 'success',
        confirmButtonColor: '#1e73be',
      });
    },
    onError: (err: any) => {
      Swal.fire({
        title: 'Backfill failed',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    },
  });

  // Export tasks (client request — Lucas): XLSX/CSV with question content and
  // a dedicated Correct Answer column, honoring the currently applied filters
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    const { value: format } = await Swal.fire({
      title: 'Export review tasks',
      input: 'radio',
      inputOptions: { xlsx: 'Excel (XLSX)', csv: 'CSV' },
      inputValue: 'xlsx',
      showCancelButton: true,
      confirmButtonText: 'Export',
      confirmButtonColor: '#1e73be',
    });
    if (!format) return;
    setExporting(true);
    try {
      const blob = await editorialWorkflowService.exportTasks(
        {
          state: appliedFilters.state || undefined,
          oeCode: appliedFilters.oeCode || undefined,
          type: appliedFilters.type || undefined,
          difficulty: appliedFilters.difficulty || undefined,
          portfolio: appliedFilters.portfolio || undefined,
          taxonomyNode: appliedFilters.taxonomyNode || undefined,
          search: appliedFilters.search || undefined,
          mine: appliedFilters.mine || undefined,
        },
        format as 'xlsx' | 'csv'
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `review_tasks_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        title: 'Export failed',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setExporting(false);
    }
  };

  // ── Row actions ───────────────────────────────────────────────────────────

  const handleAssign = async (task: ReviewTask, stage: 1 | 2) => {
    let reviewers;
    try {
      reviewers = await editorialWorkflowService.listReviewers(stage);
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not load reviewers',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    if (!reviewers.length) {
      Swal.fire({
        title: 'No reviewers available',
        text: `No active ${stage === 1 ? 'linguist' : 'professor'} users found. Create them in User Management first.`,
        icon: 'info',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const options = reviewers
      .map((r) => `<option value="${r._id}">${r.name} (${r.email})</option>`)
      .join('');

    const result = await Swal.fire({
      title: `Assign Stage ${stage} reviewer`,
      html: `<select id="reviewerSelect" class="swal2-select" style="width:80%">${options}</select>`,
      showCancelButton: true,
      confirmButtonText: 'Assign',
      confirmButtonColor: '#1e73be',
      preConfirm: () => {
        const select = document.getElementById('reviewerSelect') as HTMLSelectElement;
        return select?.value;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      await editorialWorkflowService.assignTask(task._id, stage, result.value);
      invalidate();
    } catch (err: any) {
      Swal.fire({
        title: 'Assignment failed',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const handleReject = async (task: ReviewTask) => {
    const categories = config?.rejectionCategories || [];
    const options = categories
      .map((c) => `<option value="${c.key}">${c.label}</option>`)
      .join('');

    const result = await Swal.fire({
      title: 'Reject / report question',
      html: `
        <p style="font-size:14px;color:#555;margin-bottom:8px">The question will leave the workflow and feed the generator's improvement loop.</p>
        <select id="rejectCategory" class="swal2-select" style="width:80%">${options}</select>
        <textarea id="rejectComment" class="swal2-textarea" placeholder="Comment (optional)"></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#dc2626',
      preConfirm: () => {
        const select = document.getElementById('rejectCategory') as HTMLSelectElement;
        const textarea = document.getElementById('rejectComment') as HTMLTextAreaElement;
        if (!select?.value) {
          Swal.showValidationMessage('Please select a category');
          return false;
        }
        return { category: select.value, comment: textarea?.value.trim() || undefined };
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      await editorialWorkflowService.rejectTask(
        task._id,
        result.value.category,
        result.value.comment
      );
      invalidate();
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Unexpected error',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const handleHistory = async (task: ReviewTask) => {
    let history;
    try {
      history = await editorialWorkflowService.getTaskHistory(task._id);
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not load history',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
      return;
    }

    const rows = history
      .map((h) => {
        const when = new Date(h.at).toLocaleString();
        const what =
          h.action === 'assignment'
            ? `Stage ${h.meta?.stage} assigned to ${h.meta?.assigneeName || '—'}`
            : h.fromState
              ? `${STATE_LABELS[h.fromState] || h.fromState} → ${STATE_LABELS[h.toState] || h.toState}`
              : `Task created (${STATE_LABELS[h.toState] || h.toState})`;
        const extra = h.meta?.category ? `<div style="color:#b91c1c;font-size:12px">Category: ${h.meta.category}${h.meta?.comment ? ` — ${h.meta.comment}` : ''}</div>` : '';
        return `<div style="text-align:left;border-bottom:1px solid #eee;padding:8px 0">
          <div style="font-size:13px;font-weight:600">${what}</div>${extra}
          <div style="font-size:12px;color:#777">${when} · ${h.byUserName}</div>
        </div>`;
      })
      .join('');

    Swal.fire({
      title: 'Task history',
      html: `<div style="max-height:320px;overflow-y:auto">${rows || '<p>No history</p>'}</div>`,
      confirmButtonColor: '#1e73be',
      width: 560,
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const tasks = data?.tasks || [];
  const counts = data?.counts || {};
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages || 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Review Tasks"
          description="Editorial workflow — every generated question moves through grammar and conceptual review"
          breadcrumbs={[{ label: 'Editorial Review' }, { label: 'Review Tasks' }]}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-6 py-3 bg-white border-2 border-[#1e73be] text-[#1e73be] rounded-xl font-semibold shadow hover:shadow-lg hover:bg-blue-50 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
            title="Export tasks (XLSX/CSV) with question content and a dedicated Correct Answer column — honors current filters"
          >
            {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Export
          </button>
          {canAssign && (
            <button
              onClick={() => backfillMutation.mutate()}
              disabled={backfillMutation.isPending}
              className="px-6 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Create review tasks for recently generated questions the automatic hook missed"
            >
              {backfillMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              Backfill tasks
            </button>
          )}
        </div>
      </div>

      {/* State summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {(['pending_stage_1', 'pending_stage_2', 'reviewed', 'rejected'] as EditorialTaskState[]).map(
          (state) => (
            <button
              key={state}
              onClick={() => {
                const next = { ...EMPTY_FILTERS, state: filters.state === state ? '' : state };
                setFilters(next);
                setAppliedFilters(next);
                setPage(1);
              }}
              className={`bg-white rounded-2xl shadow-md border p-4 text-left transition-all hover:shadow-lg ${
                appliedFilters.state === state ? 'border-[#1e73be] ring-1 ring-[#1e73be]' : 'border-gray-100'
              }`}
            >
              <p className="text-xs text-gray-500 mb-1">{STATE_LABELS[state]}</p>
              <p className="text-2xl font-bold text-gray-800">{counts[state] || 0}</p>
            </button>
          )
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">State</label>
            <select
              value={filters.state}
              onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              {(config?.states || []).map((s) => (
                <option key={s} value={s}>
                  {STATE_LABELS[s] || s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Search (question text)</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search stem..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">OE Code</label>
            <input
              type="text"
              value={filters.oeCode}
              onChange={(e) => setFilters((f) => ({ ...f, oeCode: e.target.value }))}
              placeholder="e.g. OE-1.2"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              <option value="multiple_choice">Multiple choice</option>
              <option value="short_answer">Short answer</option>
              <option value="case_study">Case study</option>
              <option value="interactive">Interactive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Difficulty</label>
            <select
              value={filters.difficulty}
              onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Portfolio</label>
            <select
              value={filters.portfolio}
              onChange={(e) => setFilters((f) => ({ ...f, portfolio: e.target.value, taxonomyNode: null }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All portfolios</option>
              {taskPortfolios.map((p) => (
                <option key={p._id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
            {filterPortfolioId && (
              <div className="mt-1">
                <TaxonomyCascade
                  portfolioId={filterPortfolioId}
                  value={filters.taxonomyNode}
                  onChange={(nodeId) => setFilters((f) => ({ ...f, taxonomyNode: nodeId }))}
                  compact
                />
              </div>
            )}
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.mine}
                onChange={(e) => setFilters((f) => ({ ...f, mine: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Assigned to me
            </label>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => {
                setAppliedFilters(filters);
                setPage(1);
              }}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-[#1e73be] hover:bg-[#1e73be]/90 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              Apply
            </button>
            <button
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setAppliedFilters(EMPTY_FILTERS);
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">
            {(error as any)?.response?.status === 403
              ? 'You do not have access to review tasks.'
              : (error as any)?.response?.data?.error || 'Failed to load review tasks.'}
          </div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading tasks...</span>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              No review tasks found. Newly generated questions appear here automatically
              {canAssign ? ' — or use "Backfill tasks" for existing questions.' : '.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Question</th>
                  <th className="px-4 py-3">OE</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">Stage 1</th>
                  <th className="px-4 py-3">Stage 2</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task._id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-gray-800 max-w-[320px]">
                      <Link
                        to={`/editorial/review-tasks/${task._id}`}
                        className="line-clamp-2 hover:text-[#1e73be] hover:underline"
                        title={task.questionSummary.stem}
                      >
                        {task.questionSummary.stem || '—'}
                      </Link>
                      {task.questionSummary.isVersion && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-xs text-purple-700">
                          <GitBranch className="w-3 h-3" /> Version
                        </span>
                      )}
                      {/* Base-scope parity: the question keeps this ID through the whole workflow */}
                      <div className="text-[10px] font-mono text-gray-400 mt-0.5" title="Question ID (same across Bank / Review / AI Review)">
                        ID: {String((task as any).questionId || '')}
                      </div>
                      {/* Client/request identity: portfolio + taxonomy chips */}
                      {((task.questionPortfolios || []).length > 0 || (task.questionTaxonomyPaths || []).length > 0) && (
                        <div className="flex items-center gap-1 flex-wrap mt-1">
                          {(task.questionPortfolios || []).map((pf) => (
                            <span key={pf} className="inline-flex px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700">
                              {pf}
                            </span>
                          ))}
                          {(task.questionTaxonomyPaths || []).map((path) => (
                            <span key={path} className="inline-flex px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-700">
                              {path}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {task.questionSummary.oeCode || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {task.questionSummary.type || '—'}
                      <div className="text-xs text-gray-400">{task.questionSummary.difficulty}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 rounded-lg border text-xs font-semibold ${STATE_BADGE[task.state]}`}
                      >
                        {STATE_LABELS[task.state]}
                      </span>
                      {task.state === 'rejected' && task.rejectionCategory && (
                        <div className="text-xs text-red-500 mt-1">{task.rejectionCategory}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {task.stage1Assignee?.name || <span className="text-gray-400">Unassigned</span>}
                      {task.stage1CompletedAt && (
                        <div className="text-xs text-green-600">
                          ✓ {task.stage1CompletedByName}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {task.stage2Assignee?.name || <span className="text-gray-400">Unassigned</span>}
                      {task.stage2CompletedAt && (
                        <div className="text-xs text-green-600">
                          ✓ {task.stage2CompletedByName}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                      {new Date(task.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-1">
                        {canAssign &&
                          (task.state === 'pending_stage_1' || task.state === 'pending_stage_2') && (
                            <button
                              onClick={() =>
                                handleAssign(task, task.state === 'pending_stage_1' ? 1 : 2)
                              }
                              className="p-2 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50 transition-colors"
                              title="Assign reviewer"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          )}
                        {((canStage1 && task.state === 'pending_stage_1') ||
                          (canStage2 && task.state === 'pending_stage_2')) && (
                          <Link
                            to={`/editorial/review-tasks/${task._id}`}
                            className="p-2 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors inline-flex"
                            title={`Open review (Stage ${task.state === 'pending_stage_1' ? 1 : 2})`}
                          >
                            <PenSquare className="w-4 h-4" />
                          </Link>
                        )}
                        {((canStage1 && task.state === 'pending_stage_1') ||
                          (canStage2 && task.state === 'pending_stage_2')) && (
                          <button
                            onClick={() => handleReject(task)}
                            className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Reject / report by category"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleHistory(task)}
                          className="p-2 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50 transition-colors"
                          title="Task history"
                        >
                          <History className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!error && !isLoading && (pagination?.total || 0) > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, pagination!.total)} of {pagination!.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewTasks;
